import React from 'react';
import { render, screen } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import AccountAccessTable from './AccountAccessTable';

const ACTIVE_ROW = {
  id: 1,
  decisivId: 'ACCOUNTA',
  name: 'Account A',
  dealerType: 'FLEET',
  roles: ['Fleet Administrator'],
  removedAt: null,
};

const MULTI_ROLE_ROW = {
  id: 2,
  decisivId: 'ACCOUNTB',
  name: 'Account B',
  dealerType: 'MEMBER',
  roles: ['Member', 'Manager'],
  removedAt: null,
};

const REMOVED_ROW = {
  id: 3,
  decisivId: 'ACCOUNTC',
  name: 'Account C',
  dealerType: 'BROKER',
  roles: ['Member'],
  removedAt: '2025-01-01T00:00:00Z',
};

const renderTable = (props = {}) =>
  render(
    <ConfigProvider>
      <AccountAccessTable
        canActOn={() => true}
        canEdit
        onRemove={jest.fn()}
        onRestore={jest.fn()}
        accounts={[ACTIVE_ROW]}
        {...props}
      />
    </ConfigProvider>,
  );

describe('management/EditUser AccountAccessTable', () => {
  it('titles the section', () => {
    renderTable();

    expect(
      screen.getByRole('heading', { name: 'Accounts' }),
    ).toBeInTheDocument();
  });

  it('shows the account name', () => {
    renderTable();

    expect(screen.getByText('Account A')).toBeInTheDocument();
  });

  it('shows the account id and type on one line', () => {
    renderTable();

    expect(
      screen.getByText('Account ID: ACCOUNTA · Account Type: FLEET'),
    ).toBeInTheDocument();
  });

  // Both columns are nullable on portal.dealers. The template literal behind
  // this line would otherwise print the word "null".
  describe('when an account is missing its id or type', () => {
    it('shows the placeholder rather than "null"', () => {
      renderTable({
        accounts: [{ ...ACTIVE_ROW, decisivId: null, dealerType: null }],
      });

      expect(
        screen.getByText('Account ID: — · Account Type: —'),
      ).toBeInTheDocument();
    });

    it('keeps the value it does have', () => {
      renderTable({ accounts: [{ ...ACTIVE_ROW, dealerType: null }] });

      expect(
        screen.getByText('Account ID: ACCOUNTA · Account Type: —'),
      ).toBeInTheDocument();
    });
  });

  it('shows a role tag', () => {
    renderTable();

    expect(screen.getByText('Fleet Administrator')).toBeInTheDocument();
  });

  it('shows one tag per role when an account has more than one', () => {
    renderTable({ accounts: [MULTI_ROLE_ROW] });

    expect(screen.getByText('Member')).toBeInTheDocument();
    expect(screen.getByText('Manager')).toBeInTheDocument();
  });

  describe('an active account', () => {
    it('offers a Remove Access button', () => {
      renderTable();

      expect(
        screen.getByRole('button', { name: 'Remove Access' }),
      ).toBeInTheDocument();
    });

    it('disables Remove when canActOn says no', () => {
      renderTable({ canActOn: () => false });

      expect(
        screen.getByRole('button', { name: 'Remove Access' }),
      ).toBeDisabled();
    });

    // A disabled button is otherwise just inert: the rule it enforces lives in
    // canActOn and in the server's last_active_account?, neither of which the
    // admin can see. Targets the wrapper, not the button — see GuardedCheckbox.
    it('says why Remove is disabled', () => {
      renderTable({ canActOn: () => false });

      expect(
        screen.getByLabelText(
          'Remove Access — This user must have access to at least one account. Add them to another account before removing this one.',
        ),
      ).toBeInTheDocument();
    });

    it('gives an enabled Remove no such explanation', () => {
      renderTable();

      expect(
        screen.queryByLabelText(/Remove Access —/),
      ).not.toBeInTheDocument();
    });

    it('calls onRemove with the row', () => {
      const onRemove = jest.fn();
      renderTable({ onRemove });

      userEvent.click(screen.getByRole('button', { name: 'Remove Access' }));

      expect(onRemove).toHaveBeenCalledWith(ACTIVE_ROW);
    });
  });

  describe('a removed account', () => {
    it('offers an always-enabled Restore Access button', () => {
      renderTable({ canActOn: () => false, accounts: [REMOVED_ROW] });

      expect(
        screen.getByRole('button', { name: 'Restore Access' }),
      ).not.toBeDisabled();
    });

    it('calls onRestore with the row', () => {
      const onRestore = jest.fn();
      renderTable({ onRestore, accounts: [REMOVED_ROW] });

      userEvent.click(screen.getByRole('button', { name: 'Restore Access' }));

      expect(onRestore).toHaveBeenCalledWith(REMOVED_ROW);
    });
  });

  // The design fills a removed account's row rather than marking it with a word,
  // so the fill is the only thing that says which state the row is in.
  describe('the row fill', () => {
    it('stripes an active account', () => {
      renderTable();

      expect(
        screen.getByText('Account A').closest('div').parentElement,
      ).toHaveStyleRule('background-color', '#f8f9fc');
    });

    it('shades a removed account', () => {
      renderTable({ accounts: [REMOVED_ROW] });

      expect(
        screen.getByText('Account C').closest('div').parentElement,
      ).toHaveStyleRule('background-color', '#e3e8f1');
    });
  });

  describe('Add To Account', () => {
    it('offers it, enabled', () => {
      renderTable();

      expect(
        screen.getByRole('button', { name: 'Add To Account' }),
      ).not.toBeDisabled();
    });

    // Beside the heading, not under the list: it acts on the section, and the
    // design puts it on the header row (Figma 1153:21094).
    it('sits in the section header', () => {
      renderTable();

      const header = screen
        .getByRole('heading', { name: 'Accounts' })
        .closest('div').parentElement;

      expect(header).toContainElement(
        screen.getByRole('button', { name: 'Add To Account' }),
      );
    });

    it('calls onAdd', () => {
      const onAdd = jest.fn();
      renderTable({ onAdd });

      userEvent.click(screen.getByRole('button', { name: 'Add To Account' }));

      expect(onAdd).toHaveBeenCalled();
    });
  });

  describe('when the viewer may not edit', () => {
    it('offers no action buttons', () => {
      renderTable({ canEdit: false });

      expect(
        screen.queryByRole('button', { name: /Access/ }),
      ).not.toBeInTheDocument();
    });

    it('offers no Add To Account button', () => {
      renderTable({ canEdit: false });

      expect(
        screen.queryByRole('button', { name: 'Add To Account' }),
      ).not.toBeInTheDocument();
    });
  });
});
